import { memo, useMemo, useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { format } from 'date-fns';

// レスポンシブ検出用カスタムフック
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

interface ChangeHistoryItem {
  timestamp: string;
  action?: string;
  field?: string;
  oldValue?: string;
  newValue?: string;
  userId?: string;
  changes?: {
    date?: string;
    startTime?: string;
    duration?: number;
    staff?: string;
    service?: string;
  };
}

interface Reservation {
  id: string;
  customerId: number;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  staffId: string;
  staffName: string;
  serviceId: string;
  serviceName: string;
  date: Date;
  startTime: string;
  endTime: string;
  duration: number;
  status: 'confirmed' | 'pending' | 'cancelled';
  notes?: string;
  color: string;
  changeHistory?: ChangeHistoryItem[];
  isVip?: boolean;
  price?: number;
}

interface Staff {
  id: string;
  name: string;
  color: string;
}

interface StaffColumnCalendarViewProps {
  reservations: Reservation[];
  selectedDate: Date;
  staffList: Staff[];
  onAppointmentClick: (reservation: Reservation) => void;
  onEmptySlotClick?: (time: string, staffId: string) => void;
}

// スタッフごとの色設定
const getStaffColor = (staffId: string) => {
  const staffColors: Record<string, string> = {
    'staff1': '#4CAF50', // 佐藤 - グリーン
    'staff2': '#2196F3', // 田中 - ブルー
    'staff3': '#FF9800', // 山田 - オレンジ
    'staff4': '#9C27B0', // 鈴木 - パープル
  };
  return staffColors[staffId] || '#757575';
};

// 時間を分に変換
const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};


// 時間スロット生成（15分刻み）
const generateTimeSlots = (): string[] => {
  const slots: string[] = [];
  const startHour = 9;
  const endHour = 21;
  
  for (let hour = startHour; hour < endHour; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      slots.push(time);
    }
  }
  return slots;
};

// スタッフごとの予約マップを作成
const createStaffReservationMap = (
  reservations: Reservation[], 
  selectedDate: Date,
  staffList: Staff[]
): Map<string, Map<string, Reservation | null>> => {
  const selectedDateString = format(selectedDate, 'yyyy-MM-dd');
  const staffMap = new Map<string, Map<string, Reservation | null>>();
  
  // 各スタッフの時間スロットマップを初期化
  staffList.forEach(staff => {
    const timeMap = new Map<string, Reservation | null>();
    generateTimeSlots().forEach(time => {
      timeMap.set(time, null);
    });
    staffMap.set(staff.id, timeMap);
  });
  
  // 選択日の予約をフィルタリング
  const dayReservations = reservations.filter(res => {
    const resDateString = format(res.date, 'yyyy-MM-dd');
    return resDateString === selectedDateString;
  });
  
  // 各予約を適切なスタッフと時間スロットに配置
  dayReservations.forEach(reservation => {
    const staffTimeMap = staffMap.get(reservation.staffId);
    if (!staffTimeMap) return;
    
    const startMinutes = timeToMinutes(reservation.startTime);
    const endMinutes = timeToMinutes(reservation.endTime);
    
    // 予約の開始時間から終了時間までのスロットをマーク
    generateTimeSlots().forEach(time => {
      const timeMinutes = timeToMinutes(time);
      if (timeMinutes >= startMinutes && timeMinutes < endMinutes) {
        // 開始時間のスロットのみ予約オブジェクトを設定
        if (timeMinutes === startMinutes) {
          staffTimeMap.set(time, reservation);
        } else {
          // 継続中のスロットは特別なマーカーを設定
          staffTimeMap.set(time, { ...reservation, id: `continued-${reservation.id}` });
        }
      }
    });
  });
  
  return staffMap;
};

// 予約カードの行数を計算
const calculateRowSpan = (reservation: Reservation): number => {
  const startMinutes = timeToMinutes(reservation.startTime);
  const endMinutes = timeToMinutes(reservation.endTime);
  const duration = endMinutes - startMinutes;
  return Math.ceil(duration / 15); // 15分 = 1行
};

const StaffColumnCalendarView = memo<StaffColumnCalendarViewProps>(({
  reservations,
  selectedDate,
  staffList,
  onAppointmentClick,
  onEmptySlotClick
}) => {
  const isMobile = useIsMobile();

  // 時間スロット生成
  const timeSlots = useMemo(() => generateTimeSlots(), []);

  // スタッフごとの予約マップ作成
  const staffReservationMap = useMemo(() =>
    createStaffReservationMap(reservations, selectedDate, staffList),
    [reservations, selectedDate, staffList]
  );

  // 処理済みの予約IDを追跡
  const processedReservations = new Set<string>();

  return (
    <div>
      <div className="max-h-[70vh] overflow-auto rounded-md border">
        <Table>
          <TableHeader className="sticky top-0 bg-background z-10">
            <TableRow>
              <TableCell
                className="sticky left-0 z-30 bg-background min-w-[80px] font-bold border-r"
              >
                時刻
              </TableCell>
              {staffList.map(staff => (
                <TableCell
                  key={staff.id}
                  className={`text-center bg-background ${isMobile ? 'min-w-[120px]' : 'min-w-[150px]'}`}
                >
                  <Badge
                    variant="outline"
                    className="font-bold"
                    style={{
                      backgroundColor: `${getStaffColor(staff.id)}33`,
                      color: getStaffColor(staff.id),
                      borderColor: getStaffColor(staff.id),
                    }}
                  >
                    {staff.name}
                  </Badge>
                </TableCell>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {timeSlots.map((time) => {
              return (
                <TableRow key={time}>
                  <TableCell
                    className="sticky left-0 bg-background font-medium text-sm text-muted-foreground z-10 border-r"
                  >
                    {time}
                  </TableCell>
                  {staffList.map(staff => {
                    const staffTimeMap = staffReservationMap.get(staff.id);
                    const reservation = staffTimeMap?.get(time);
                    
                    // 継続中のスロットはスキップ（rowSpanで処理済み）
                    if (reservation && reservation.id.startsWith('continued-')) {
                      return null;
                    }
                    
                    // 予約がある場合
                    if (reservation && !processedReservations.has(reservation.id)) {
                      processedReservations.add(reservation.id);
                      const rowSpan = calculateRowSpan(reservation);
                      const staffColor = getStaffColor(staff.id);

                      return (
                        <TableCell
                          key={`${time}-${staff.id}`}
                          rowSpan={rowSpan}
                          className="p-0.5 align-top transition-colors hover:bg-opacity-10"
                          style={{
                            backgroundColor: `${staffColor}0d`,
                          }}
                        >
                          <Card
                            data-time={time}
                            onClick={() => onAppointmentClick(reservation)}
                            className="cursor-pointer h-full transition-all hover:scale-[1.02] hover:shadow-md border-l-4"
                            style={{
                              minHeight: rowSpan * 35,
                              backgroundColor: `${staffColor}26`,
                              borderLeftColor: staffColor,
                            }}
                          >
                            <div className="p-1">
                              <span
                                className="text-xs font-bold block"
                                style={{ color: staffColor }}
                              >
                                {reservation.startTime} - {reservation.endTime}
                              </span>
                              <p className="text-sm font-semibold mt-0.5">
                                {reservation.customerName}
                              </p>
                              <span className="text-xs text-muted-foreground block mt-0.5">
                                {reservation.serviceName}
                              </span>
                            </div>
                          </Card>
                        </TableCell>
                      );
                    }
                    
                    // 空きスロット
                    return (
                      <TableCell
                        key={`${time}-${staff.id}`}
                        className="p-1 bg-gray-50 cursor-pointer hover:bg-primary/5 transition-colors"
                        onClick={() => onEmptySlotClick?.(time, staff.id)}
                      >
                        <div className="min-h-[30px] flex items-center justify-center">
                          <span className="text-xs text-muted-foreground/50">
                            -
                          </span>
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* モバイル用の横スクロールヒント */}
      {isMobile && (
        <p className="text-xs text-center text-muted-foreground mt-1">
          ← 左右にスクロールできます →
        </p>
      )}
    </div>
  );
});

StaffColumnCalendarView.displayName = 'StaffColumnCalendarView';

export default StaffColumnCalendarView;